import {
  closestCorners,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";
import ActionButton from "components/common/ActionButton";
import MultiSelect from "components/common/MultiSelect";
import React, { useState } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td
        {...listeners}
        {...attributes}
        style={{
          cursor: "grab",
          textAlign: "center",
          width: "10px",
          // paddingTop: "15px",
        }}
      >
        ☰
      </td>
      {children}
    </tr>
  );
};

const BasicInformation = ({ apiLoading }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const {
    register,
    setValue,
    clearErrors,
    control,
    watch,
    dirtyFields,
    formState: { errors },
  } = useFormContext();

  const {
    fields: details,
    append: appendDetails,
    remove: removeDetails,
    move,
  } = useFieldArray({
    control,
    name: "details",
  });

  const [loading, setLoading] = useState(false);
  const maxCharactersLength = 255;
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = details.findIndex((item) => item.id === active.id);
      const newIndex = details.findIndex((item) => item.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  const detailsWatch = useWatch({
    control,
    name: "details",
  });
  const totalWeight = React.useMemo(() => {
    if (!detailsWatch) return 0;
    return detailsWatch.reduce((acc, item) => {
      const weight = parseFloat(item.weight);
      return acc + (isNaN(weight) ? 0 : weight);
    }, 0);
  }, [detailsWatch]);

  return (
    <>
      {loading ? (
        <div className="d-flex py-3 justify-content-center align-items-center">
          <Button variant="falcon-default" size="sm" className="fs-10">
            Loading...
          </Button>
        </div>
      ) : (
        <Card.Body
          className=""
          style={{
            height: `calc(100vh - 8rem)`,
            overflow: "auto",
            padding: "5px 20px",
          }}
        >
          <Row className="gx-2 pb-2">
            {/* Left side with 3 small inputs */}
            <Col md="8">
              <Row className="gx-2 pb-2">
                <Col md="9">
                  <Form.Group>
                    <Form.Select
                      aria-label="turn"
                      title="Turn"
                      className="py-2 mb-1"
                      isInvalid={!!errors?.turn}
                      disabled
                      {...register("turn")}
                    >
                      <option value="">Select Turn</option>
                      <option value="First">First</option>
                      <option value="Second">Second</option>
                      <option value="Third">Third</option>
                      <option value="Final">Final</option>
                      <option value="Punch">Punch</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors?.turn?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md="3">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      placeholder="Number"
                      title="Number"
                      maxLength={maxCharactersLength}
                      className="py-2"
                      isInvalid={!!errors.number}
                      {...register("number")}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.number?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md="9">
                  <Form.Group>
                    <Form.Control
                      type="date"
                      placeholder="Date"
                      title="Date"
                      className="py-2 mb-1"
                      isInvalid={!!errors.date}
                      {...register("date")}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.date?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md="3">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      placeholder="Vehicle Number"
                      title="Vehicle Number"
                      maxLength={maxCharactersLength}
                      className="py-2 "
                      isInvalid={!!errors.vehicleNumber}
                      {...register("vehicleNumber")}
                    />
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Control.Feedback
                        type="invalid"
                        className="d-inline w-75"
                      >
                        {errors.vehicleNumber?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </Col>

                <Col md="9">
                  <Form.Group>
                    <Form.Select
                      aria-label="party"
                      type="text"
                      title="Party"
                      className="py-2"
                      isInvalid={!!errors?.party}
                      {...register("party")}
                    >
                      <option value="">Select Party</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors?.party?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md="3">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      placeholder="Driver Contact Number"
                      title="Driver Contact Number"
                      maxLength={maxCharactersLength}
                      className="py-2 "
                      isInvalid={!!errors.driverContactNumber}
                      {...register("driverContactNumber")}
                    />
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Control.Feedback
                        type="invalid"
                        className="d-inline w-75"
                      >
                        {errors.driverContactNumber?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </Col>
                <Col md="9">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      placeholder="Driver Name"
                      title="Driver Name"
                      maxLength={maxCharactersLength}
                      className="py-2 "
                      isInvalid={!!errors.driverName}
                      {...register("driverName")}
                    />
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Control.Feedback
                        type="invalid"
                        className="d-inline w-75"
                      >
                        {errors.driverName?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Col>

            {/* Right side with Weight field spanning 3 rows */}
            <Col md="4" className="d-flex">
              <Form.Group className="h-100">
                <Form.Control
                  type="text"
                  placeholder="Weight"
                  title="Weight"
                  maxLength={maxCharactersLength}
                  className="fw-bold text-end "
                  isInvalid={!!errors.weight}
                  disabled
                  value={Number(register("weight").value || 0).toFixed(2)}
                  style={{
                    height: "133px",
                    color: "green",
                    fontSize: "100px",
                  }} // adjust height as needed
                  {...register("weight")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.weight?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="gx-2 pb-2 align-items-stretch">
            {/* <Col md="6">
              <Form.Group>
                <Form.Select
                  aria-label="turn"
                  type="text"
                  title="Turn"
                  className="py-2"
                  isInvalid={!!errors?.turn}
                  {...register("turn")}
                >
                  <option value="">Select Turn</option>
                  <option value="First">First</option>
                  <option value="Second">Second</option>
                  <option value="Third">Third</option>
                  <option value="Final">Final</option>
                  <option value="Punch">Punch</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.turn?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md="2">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Number"
                  title="Number"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.number}
                  {...register("number")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.number?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>

            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="date"
                  placeholder="Date"
                  title="Date"
                  className="py-2 mb-1"
                  isInvalid={!!errors.date}
                  {...register("date")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.date?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md="2">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Vehicle Number"
                  title="Vehicle Number"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.vehicleNumber}
                  {...register("vehicleNumber")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.vehicleNumber?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Select
                  aria-label="party"
                  type="text"
                  title="Party"
                  className="py-2"
                  isInvalid={!!errors?.party}
                  {...register("party")}
                >
                  <option value="">Select Party</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.party?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md="2">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Driver Contact Number"
                  title="Driver Contact Number"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.driverContactNumber}
                  {...register("driverContactNumber")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.driverContactNumber?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Driver Name"
                  title="Driver Name"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.driverName}
                  {...register("driverName")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.driverName?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col> */}

            {/* <Col md="6">
              <Form.Check
                type="checkbox"
                id="defaultSwitch"
                label="Active"
                className="mt-2"
                defaultChecked={!state}
                isInvalid={!!errors.status}
                {...register("status")}
              />
            </Col> */}

            <Row className="gx-2">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={details.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div
                    style={{
                      maxHeight: `calc(100vh - 20.7rem)`,
                      overflowY: "auto",
                      // border: "1px solid #ddd",
                    }}
                  >
                    <Table
                      className=""
                      style={{ borderCollapse: "separate", borderSpacing: 0 }}
                    >
                      {/* 🔹 Fixed Header */}
                      <thead
                        className="bg-200"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          background: "#fff",
                        }}
                      >
                        <tr>
                          <th
                            scope="col"
                            className="w-0 fs-10 text-center"
                          ></th>
                          <th
                            scope="col"
                            className="fs-10 text-center"
                            style={{ width: "140px" }}
                          >
                            Sr. Number
                          </th>
                          <th scope="col" className="fs-10">
                            Product
                          </th>
                          <th
                            scope="col"
                            className="fs-10 text-end"
                            style={{ width: "180px" }}
                          >
                            Weight
                          </th>
                          <th
                            scope="col"
                            style={{ padding: "9px", width: "10px" }}
                            className="text-end"
                          >
                            <ActionButton
                              icon="plus"
                              variant="success"
                              title="Add"
                              className="px-1 py-0"
                              onClick={() => appendDetails({})}
                            />
                          </th>
                        </tr>
                      </thead>

                      {/* 🔹 Scrollable Table Body */}
                      <tbody>
                        {details.map((item, index) => (
                          <SortableItem key={item.id} id={item.id}>
                            <td className="text-center">{index + 1}</td>
                            <td style={{ padding: "10px" }}>
                              <Form.Group>
                                <Form.Select
                                  aria-label="product"
                                  type="text"
                                  title="Product"
                                  isInvalid={!!errors.details?.[index]?.product}
                                  {...register(`details[${index}].product`)}
                                >
                                  <option value="">Select Product</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  {errors.details?.[index]?.product?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </td>

                            <td style={{ paddingTop: "10px" }}>
                              <Form.Control
                                type="number"
                                placeholder="Weight"
                                className="text-end"
                                isInvalid={!!errors.details?.[index]?.weight}
                                {...register(`details[${index}].weight`)}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.details?.[index]?.weight?.message}
                              </Form.Control.Feedback>
                            </td>
                            <td
                              style={{ paddingLeft: "9px", maxWidth: "10px" }}
                              className="text-center"
                            >
                              <ActionButton
                                icon="trash-alt"
                                title="Delete"
                                variant="danger"
                                className="px-1 py-0"
                                onClick={() => removeDetails(index)}
                              />
                            </td>
                          </SortableItem>
                        ))}
                      </tbody>

                      {/* 🔹 Fixed Footer */}
                      <tfoot
                        style={{
                          position: "sticky",
                          bottom: 0,
                          zIndex: 10,
                          background: "#fff",
                        }}
                      >
                        <tr>
                          <th
                            scope="col"
                            className="w-0 fs-10 text-center"
                          ></th>
                          <th
                            scope="col"
                            className="fs-10 text-center"
                            style={{ width: "140px" }}
                          ></th>

                          <th scope="col" className="fs-10 fw-bold">
                            Total
                          </th>
                          <th
                            scope="col"
                            className="fs-10 text-end  fw-bold"
                            style={{ width: "180px" }}
                          >
                            {`${totalWeight.toFixed(2)} kg`}
                          </th>

                          <th
                            scope="col"
                            style={{ padding: "9px", width: "10px" }}
                            className="text-end"
                          ></th>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </SortableContext>
              </DndContext>
            </Row>
          </Row>
        </Card.Body>
      )}
    </>
  );
};

export default React.memo(BasicInformation);

//  <Col md="4">
//             <Form.Group className="h-100">
//               <Form.Control
//                 type="text"
//                 placeholder="Weight"
//                 title="Weight"
//                 maxLength={maxCharactersLength}
//                 className="h-100 py-12"
//                 isInvalid={!!errors.weight}
//                 {...register("weight")}
//               />
//               <Form.Control.Feedback type="invalid">
//                 {errors.weight?.message}
//               </Form.Control.Feedback>
//             </Form.Group>
//           </Col>
